<template>
  <wizard
    first-step="termsAndConditions"
    class="wizard-cancel-upcoming-contract"
  >
    <wizard-step
      :i18n="i18n"
      name="termsAndConditions"
      :model="formData"
      next-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.termsAndConditions.title }}</h2>
        <slot name="terms-and-conditions-paragraph" />
      </wizard-step-intro>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      :next-step="submitPersonalDetails"
      previous-step="termsAndConditions"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.personalDetails.title }}</h2>
        <slot name="personal-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="bonusCode"
          type="text"
          label="bonus code"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          name="website"
          type="text"
          label="website"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          :label="i18n.form.labels.name"
          name="name"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.name"
        />

        <FormulateInput
          name="email"
          type="email"
          :label="i18n.form.labels.email"
          validation="bail|required:trim|email"
          :validation-messages="i18n.form.validations.email"
        />

        <FormulateInput
          name="phoneNumber"
          type="text"
          :label="i18n.form.labels.phoneNumber"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.phoneNumber"
        />

        <FormulateInput
          type="text"
          name="debtorNumber"
          :label="i18n.form.labels.debtorNumber"
          :help="i18n.form.help.debtorNumber"
          validation="bail|required:trim|max:9,length|min:6,length|nohtml|matches:/^[0-9\-]+$/"
          validation-name="Klantnummer"
          :validation-messages="i18n.form.validations.debtorNumber"
          @keydown.space.prevent
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="selectChildren"
      :model="formData"
      :next-step="submitSelectedChildren"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.selectChildren.title }}</h2>
        <slot name="select-children-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <h3>{{ i18n.form.labels.childRegistrationItems }}</h3>

        <FormulateInput
          type="group"
          name="children"
          :help="i18n.form.help.childRegistrationForm"
          :add-label="i18n.form.addlabel.childRegistrationForm"
          :repeatable="true"
          validation="bail|min:1,length|max:5,length"
          :validation-messages="i18n.form.validations.childRegistrationItems"
          @repeatableRemoved="resetChangesPerChild"
        >
          <div class="row">
            <!-- workaround for losing focus on first input -->
            <FormulateInput
              type="hidden"
              value="none"
            />
            <!-- /workaround -->
            <FormulateInput
              type="text"
              name="name"
              :label="i18n.form.labels.childName"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.childName"
            />
          </div>
        </FormulateInput>
      </fieldset>
    </wizard-step>

    <wizard-step
      v-for="(indexPlusOne, index) in 5"
      :key="index"
      :i18n="i18n"
      :name="'changesForChild-' + index"
      :model="formData"
      :next-step="
        indexPlusOne < formData.selectChildren.children.length
          ? 'changesForChild-' + indexPlusOne
          : 'reasonOfTermination'
      "
      :previous-step="index > 0 ? 'changesForChild-' + (index - 1) : 'selectChildren'"
    >
      <wizard-step-intro>
        <h2>
          {{
            i18n.wizardSteps.changesPerChild.title.replace(
              '{name child}',
              formData.selectChildren.children[index]
                ? formData.selectChildren.children[index].name
                : '',
            )
          }}
        </h2>
        <slot name="changes-per-child-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="productTypes"
          :options="{
            preschoolcare: i18n.form.labels.productTypeOptions.preschoolcare,
            afterschoolcare: i18n.form.labels.productTypeOptions.afterschoolcare,
            daycare: i18n.form.labels.productTypeOptions.daycare,
            toddlercare: i18n.form.labels.productTypeOptions.toddlercare,
          }"
          type="checkbox"
          :label="i18n.form.labels.productType"
          validation="required"
          :validation-messages="i18n.form.validations.productType"
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="reasonOfTermination"
      :model="formData"
      next-step="remark"
      :previous-step="'changesForChild-' + (formData.selectChildren.children.length - 1)"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.reasonOfTermination.title }}</h2>
        <slot name="reason-of-termination-paragraph" />
      </wizard-step-intro>

      <FormulateInput
        name="reasonofterminationselected"
        :options="{
          differentdaycare: i18n.form.labels.reasonofterminationoptions.differentdaycare,
          situation: i18n.form.labels.reasonofterminationoptions.situation,
          cost: i18n.form.labels.reasonofterminationoptions.cost,
          undisclosed: i18n.form.labels.reasonofterminationoptions.undisclosed,
          other: i18n.form.labels.reasonofterminationoptions.other,
        }"
        type="radio"
        :label="i18n.form.labels.reasonoftermination"
        validation="required"
        :validation-messages="i18n.form.validations.reasonoftermination"
      />
      <FormulateInput
        v-if="isOtherReasonOfTerminationSelected"
        name="reasonofterminationotherreason"
        type="textarea"
        validation="bail|required:trim|nohtml"
        :validation-messages="i18n.form.validations.reasonofterminationotherreason"
      />
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="remark"
      :model="formData"
      next-step="review"
      previous-step="reasonOfTermination"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.remark.title }}</h2>
        <slot name="remark-paragraph" />
      </wizard-step-intro>

      <fields-remark
        :i18n="i18n"
        :model="formData.remark"
      />
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="review"
      :model="formData"
      :next-step="sendRequest"
      previous-step="remark"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.review.title }}</h2>
        <slot name="review-paragraph" />
      </wizard-step-intro>

      <table class="wizard-summary">
        <tbody>
          <tr>
            <th>{{ i18n.form.labels.reviewName }}</th>
            <td>{{ resultingData.personalDetails.name }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewEmail }}</th>
            <td>{{ resultingData.personalDetails.email }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewPhoneNumber }}</th>
            <td>{{ resultingData.personalDetails.phoneNumber }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewCustomerNumber }}</th>
            <td>{{ resultingData.personalDetails.debtorNumber }}</td>
          </tr>
          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>

          <template v-for="(child, childIndex) of resultingData.changesPerChild">
            <tr :key="'reviewName-' + childIndex">
              <td colspan="2">
                <h3>{{ child.childName }}</h3>
              </td>
            </tr>
            <tr
              v-if="child.productChanges.productTypes"
              :key="'reviewProductTypes-' + childIndex"
            >
              <th>{{ i18n.form.labels.reviewProductTypes }}</th>
              <td>
                {{
                  child.productChanges.productTypes
                    .map((d) => i18n.form.labels.productTypeOptions[d])
                    .join(', ')
                }}
              </td>
            </tr>
            <tr :key="'reviewRemark-' + childIndex">
              <th>{{ i18n.form.labels.reviewRemark }}</th>
              <td>{{ child.productChanges.remark }}</td>
            </tr>
            <tr
              v-if="childIndex < resultingData.changesPerChild.length - 1"
              :key="'reviewLineBreak-' + childIndex"
            >
              <td colspan="2">
                {{ '\xa0' }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </wizard-step>
  </wizard>
</template>

<script>
  import FieldsRemark from '@/components/forms/fields/FieldsRemark.vue';
  import Wizard from '@/components/wizards/components/Wizard.vue';
  import WizardStep from '@/components/wizards/components/WizardStep.vue';
  import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
  import getQueryParams from '@/lib/formulate/getFormQueryParam';
  import axios from 'axios';

  export default {
    name: 'WizardCancelUpcomingContract',
    components: {
      FieldsRemark,
      Wizard,
      WizardStep,
      WizardStepIntro,
    },
    props: {
      i18n: {
        type: Object,
        default: null,
      },
      prefillData: {
        type: Object,
        default: () => { },
      },
      locale: {
        type: String,
        default: () => 'nl',
      },
      label: {
        type: String,
        default: 'Partou',
      },
      successPageUrl: {
        type: String,
        default: () => '/',
      },
      formToken: {
        type: String,
        default: () => '',
      },
    },
    data() {
      return {
        formData: {
          personalDetails: this.$store.state.wizard.personalDetails,
          selectChildren: this.$store.state.wizard.selectedChildren,
          reasonOfTermination: {},
          remark: {},
        },
        request: {
          isSending: false,
          errorSending: false,
        },
      };
    },
    computed: {
      isOtherReasonOfTerminationSelected() {
        return this.formData.reasonOfTermination.reasonofterminationselected === 'other';
      },
      resultingData() {
        return {
          label: this.label,
          locale: this.locale,
          formToken: this.formToken,
          personalDetails: this.formData.personalDetails,
          changesPerChild: this.formData.selectChildren.children.map((child, childIndex) => {
            const changeData = this.formData[`changesForChild-${childIndex}`];
            return {
              childName: child.name,
              productChanges: {
                productTypes: changeData?.productTypes,
              },
            };
          }),
          reasonOfTermination: this.formData.reasonOfTermination,
          remarkContact: this.formData.remark,
        };
      },
    },
    created() {
      this.formData.personalDetails = {
        ...this.formData.personalDetails,
        ...getQueryParams(this.prefillData)
      }
    },
    methods: {
      submitPersonalDetails() {
        this.$store.dispatch('wizard/setPersonalDetails', this.formData.personalDetails);
        return 'selectChildren';
      },
      submitSelectedChildren() {
        this.$store.dispatch('wizard/setSelectedChildren', this.formData.selectChildren);
        return 'changesForChild-0';
      },
      resetChangesPerChild() {
        Object.keys(this.formData)
          .filter((k) => k.startsWith('changesForChild-'))
          .forEach((k) => (this.formData[k] = {}));
      },
      sendRequest() {
        this.request.errorSending = false;
        this.request.isSending = true;

        const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

        const config = {
          headers: {
            'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
          }
        };

        axios
          .post('/umbraco/api/wizard/cancelupcomingcontract', this.resultingData, config)
          .then(() => {
            window.location.href = this.successPageUrl;
          })
          .catch(() => {
            this.request.isSending = false;
            this.request.errorSending = true;
          });
      },
    },
  };
</script>
